import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { revisarCierre, getCierreDetalle, registrarDatosSistema, corregirVentasCajero, habilitarCorreccion, resolverConciliacion, responderSolicitudCorreccion, buscarTiposGasto, resolverTipoGasto, buscarBeneficiariosGasto, resolverBeneficiarioGasto } from '../../services/data';
import { toMoney, parseDinero, sumarDinero } from '../../utils/numbers';
import { nombreTurno } from '../../utils/turnos';
import { exportSingleCuadrePdf } from '../../pdf/exportadores';
import { exportCierreDetallePdf } from '../../pdf/cierreDetallePdf';
import { formatDate, formatDateTime, formatTime } from '../../utils/dates';
import { fechaCorta } from '../finanzas/estados';
import { resultadoCierre, revisionCierre, textoResultado, claseResultado, claseRevision } from './estadoCierre';
import '../finanzas/ui.css';
import LiquidacionSeccion from './LiquidacionSeccion';
import { revisarProveedorCompra, revisarProductoCompra } from './revisionCatalogo';
import { ComprobanteLink } from '../finanzas/Comprobante';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { PERMISO_CORRECCION_EXCEPCIONAL, tienePermiso } from '../../utils/permisos';
import { serviciosDe } from '../../utils/servicios';
import MoneyInput from '../common/MoneyInput';
import { marcarCambiosPendientes } from '../../services/version';
import { cuadreVista, notaFondo, resumirCompras, formaPagoCompra, cantidadItem, unitarioItem } from './cuadreVista';
import FondoModal from '../common/FondoModal';

const n = (v) => Number(v) || 0;
// Clave de lo editable con guardado automático (datos del sistema + Observación) para saber si hay cambios sin guardar.
const CAMPOS_SISTEMA = ['efectivo', 'tarjeta', 'pedidosYa'];
const claveAuto = (sis, comentario) => JSON.stringify({ efectivo: sis.efectivo, tarjeta: sis.tarjeta, pedidosYa: sis.pedidosYa, comentario });
const SALIR_SIN_GUARDAR = 'Hay cambios sin guardar. ¿Deseas salir sin guardarlos?';
const hora = formatDateTime;
// Tarjetas Apertura/Cierre: solo la hora (la fecha ya está en el título).
const soloHora = formatTime;
const Dif = ({ v }) => (v == null ? <span className="fx-empty">Pendiente</span>
  : <b style={{ color: Math.abs(v) < 0.005 ? 'var(--ok)' : v < 0 ? 'var(--danger)' : 'var(--info)' }}>{v > 0 ? '+' : ''}{toMoney(v)}</b>);

// Revisión administrativa de un cuadre: lo reportado por el cajero, los datos del sistema,
// el cálculo interno de caja, compras, gastos/pagos, depósitos y trazabilidad.
export default function CierreDetalleModal({ cierreId, onClose, onChanged, onCorregir }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [sistema, setSistema] = useState({ efectivo: '', tarjeta: '', pedidosYa: '' });
  const [comentario, setComentario] = useState('');
  const [busy, setBusy] = useState(false);
  const [guardado, setGuardado] = useState(''); // '', 'guardando', 'ok', 'error'
  const [guardadoAt, setGuardadoAt] = useState(null); // último guardado automático confirmado por el servidor
  // Lo que está en la base de datos (clave de los campos con guardado automático). Solo cambia al cargar o con un guardado exitoso.
  const persistido = useRef('');
  const [persistidoClave, setPersistidoClave] = useState('');
  const marcarPersistido = (k) => { persistido.current = k; setPersistidoClave(k); };
  const timer = useRef(null);

  const cargar = useCallback(async ({ soloResultados = false } = {}) => {
    try {
      const r = await getCierreDetalle(cierreId);
      setD(r);
      if (!soloResultados) { // tras un guardado automático no se pisa lo que el administrador sigue escribiendo
        const s = r.cierre.cierre?.[0] || {};
        const sis = { efectivo: s.efectivo ?? '', tarjeta: s.tarjeta ?? '', pedidosYa: s.pedidosYa ?? '' };
        setSistema(sis);
        setComentario(r.cierre.comentario || '');
        marcarPersistido(claveAuto(sis, r.cierre.comentario || ''));
      }
      setError('');
    } catch (e) { setError(e.message || 'No se pudo cargar el cuadre'); }
  }, [cierreId]);
  useEffect(() => { cargar(); }, [cargar]);

  // Guardado automático de los datos del sistema y de la Observación: al dejar de escribir (700 ms) y al salir del campo.
  // Montos del sistema: el servidor recalcula diferencias, comisiones, neto y estado (aquí se refrescan los resultados).
  // Solo la Observación: se guarda sin tocar los montos ni recalcular (es informativa).
  // Por qué no se puede guardar todavía (montos incompletos); null = se puede.
  const motivoNoGuardable = (datos) => {
    const antes = JSON.parse(persistido.current || '{}');
    if (!CAMPOS_SISTEMA.some((k) => (antes[k] ?? '') !== datos[k])) return null; // solo cambió la observación
    if (String(datos.efectivo).trim() === '') return 'falta el efectivo del sistema';
    if (CAMPOS_SISTEMA.some((k) => String(datos[k]).trim() !== '' && parseDinero(datos[k]) === null) || /\.$/.test(datos.efectivo + datos.tarjeta + datos.pedidosYa))
      return 'revisa los montos del sistema';
    return null;
  };
  const enCurso = useRef(Promise.resolve(true));
  // Devuelve true si todo quedó guardado (o no había nada que guardar).
  const guardarAuto = useCallback((datos) => {
    clearTimeout(timer.current);
    enCurso.current = enCurso.current.then(async () => {
      const clave = claveAuto(datos, datos.comentario);
      if (clave === persistido.current) return true;
      if (motivoNoGuardable(datos)) return false;
      const antes = JSON.parse(persistido.current || '{}');
      const cambiaSistema = CAMPOS_SISTEMA.some((k) => (antes[k] ?? '') !== datos[k]);
      setGuardado('guardando');
      try {
        await registrarDatosSistema(cierreId, cambiaSistema
          ? { efectivo: datos.efectivo, tarjeta: datos.tarjeta, pedidosYa: datos.pedidosYa, comentario: datos.comentario }
          : { comentario: datos.comentario });
        marcarPersistido(clave);
        setGuardado('ok'); setGuardadoAt(new Date());
        if (cambiaSistema) await cargar({ soloResultados: true });
        onChanged?.();
        return true;
      } catch (err) {
        setGuardado('error');
        Swal.fire('No se pudo guardar', err.message || 'Intenta de nuevo.', 'error');
        return false;
      }
    });
    return enCurso.current;
  }, [cierreId, cargar, onChanged]); // eslint-disable-line react-hooks/exhaustive-deps
  const programar = (datos) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => guardarAuto(datos), 700);
  };
  // Al cerrar el detalle no se guarda a escondidas: salir pasa por pedirCerrar (guarda o pregunta).
  useEffect(() => () => clearTimeout(timer.current), []);
  const cambiarSistema = (k, v) => { const sis = { ...sistema, [k]: v }; setSistema(sis); programar({ ...sis, comentario }); };
  const cambiarComentario = (v) => { setComentario(v); programar({ ...sistema, comentario: v }); };
  const alSalir = () => guardarAuto({ ...sistema, comentario });

  // Responder la solicitud de corrección del cajero: aprobar (habilita el cierre) o rechazar (comentario obligatorio).
  const responder = async (s, aprobar) => {
    const { value, isConfirmed } = await Swal.fire({ title: aprobar ? 'Aprobar corrección' : 'Rechazar solicitud', input: 'textarea',
      inputLabel: aprobar ? 'Comentario (opcional)' : 'Motivo del rechazo', text: `Motivo del cajero: ${s.motivo}`, showCancelButton: true,
      confirmButtonText: aprobar ? 'Aprobar y habilitar' : 'Rechazar', inputValidator: (v) => (!aprobar && !v?.trim() ? 'Indica el motivo del rechazo' : undefined) });
    if (!isConfirmed) return;
    setBusy(true);
    try { await responderSolicitudCorreccion(s.id, aprobar ? 'aprobar' : 'rechazar', (value || '').trim()); await cargar(); onChanged?.(); }
    catch (err) { Swal.fire('Error', err.message || 'No se pudo responder.', 'error'); }
    finally { setBusy(false); }
  };

  // Corregir lo que reportó el cajero (solo administración). null = sin editar.
  // Se puede corregir un solo valor: solo viajan los campos que cambiaron; vacío = se conserva el valor guardado.
  const [edicion, setEdicion] = useState(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const iniciarEdicion = (actuales) => {
    const originales = Object.fromEntries(Object.entries(actuales).map(([k, v]) => [k, Number(v).toFixed(2)]));
    setEdicion({ originales, valores: { ...originales }, motivo: '' });
  };
  const cancelarEdicion = () => setEdicion(null); // descarta lo escrito: la tabla vuelve a los valores guardados
  const edicionConCambios = !!edicion && (!!edicion.motivo.trim()
    || Object.entries(edicion.valores).some(([k, v]) => String(v ?? '').trim() !== '' && parseDinero(v) !== parseDinero(edicion.originales[k])));
  const guardarVentasCajero = async () => {
    if (guardandoEdicion) return;
    const cambios = {};
    for (const [k, v] of Object.entries(edicion.valores)) {
      if (String(v ?? '').trim() === '') continue;
      const monto = parseDinero(v);
      if (monto === null) return Swal.fire('Corregir valores del cajero', 'Revisa los importes: usa números con hasta dos decimales.', 'warning');
      if (monto !== parseDinero(edicion.originales[k])) cambios[k] = monto.toFixed(2);
    }
    if (!Object.keys(cambios).length) return Swal.fire('Corregir valores del cajero', 'No cambiaste ningún valor.', 'info');
    if (!edicion.motivo.trim()) return Swal.fire('Corregir valores del cajero', 'Indica el motivo de la corrección.', 'warning');
    setGuardandoEdicion(true);
    try {
      await corregirVentasCajero(cierreId, { ...cambios, motivo: edicion.motivo.trim() });
      // Primero se recarga lo guardado en el servidor; después se sale del modo edición mostrando los valores nuevos.
      await cargar({ soloResultados: true });
      setEdicion(null);
      onChanged?.();
    } catch (err) { Swal.fire('No se pudo corregir', err.message || '', 'error'); } // sigue en edición con lo escrito
    finally { setGuardandoEdicion(false); }
  };

  const habilitar = async () => {
    const excepcional = !!d?.correccionExcepcional?.requerida;
    const { value: motivo } = await Swal.fire(excepcional
      ? { title: 'Corrección administrativa excepcional', icon: 'warning', input: 'textarea', inputLabel: 'Justificación (obligatoria)',
        text: 'Este cierre tiene liquidaciones, depósitos o movimientos bancarios registrados. Modificarlo podría generar diferencias entre el efectivo registrado, los movimientos realizados y los saldos financieros. ¿Desea habilitar la corrección de todas formas?',
        showCancelButton: true, confirmButtonText: 'Autorizar corrección', cancelButtonText: 'Cancelar', reverseButtons: true, focusCancel: true,
        inputValidator: (v) => (!v?.trim() ? 'Indica la justificación' : undefined) }
      : { title: 'Habilitar corrección', input: 'text', inputLabel: 'Motivo (queda en la trazabilidad)',
        text: 'El cajero podrá corregir y volver a cerrar este cuadre.', showCancelButton: true, confirmButtonText: 'Habilitar',
        inputValidator: (v) => (!v?.trim() ? 'Indica el motivo' : undefined) });
    if (!motivo) return;
    setBusy(true);
    try { await habilitarCorreccion(cierreId, motivo.trim(), excepcional); await cargar(); onChanged?.(); }
    catch (err) { Swal.fire('Error', err.message || 'No se pudo habilitar.', 'error'); }
    finally { setBusy(false); }
  };

  // La conciliación solo se documenta aquí: los ajustes de dinero se hacen con sus operaciones (Liquidación, Bancos).
  const conciliar = async () => {
    const { value: comentario } = await Swal.fire({ title: 'Marcar como conciliado', input: 'textarea', inputLabel: 'Cómo se resolvió la diferencia',
      text: 'No modifica liquidaciones, depósitos ni bancos.', showCancelButton: true, confirmButtonText: 'Marcar como conciliado', cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v?.trim() ? 'Indica cómo se resolvió' : undefined) });
    if (!comentario) return;
    setBusy(true);
    try { await resolverConciliacion(cierreId, comentario.trim()); await cargar(); onChanged?.(); }
    catch (err) { Swal.fire('Error', err.message || 'No se pudo marcar como conciliado.', 'error'); }
    finally { setBusy(false); }
  };

  // Catálogo de proveedor/producto: diálogos compartidos con el panel de Compras.
  const revisarCatalogo = async (fn) => {
    try { setBusy(true); if (await fn()) { await cargar({ soloResultados: true }); onChanged?.(); } }
    catch (err) { Swal.fire('Error', err.message || 'No se pudo actualizar el catálogo.', 'error'); }
    finally { setBusy(false); }
  };
  const revisarProveedor = (compra) => revisarCatalogo(() => revisarProveedorCompra(compra));
  const revisarProducto = (item) => revisarCatalogo(() => revisarProductoCompra(item));

  const revisarTipo = async (g, indice) => {
    try {
      const tipos = await buscarTiposGasto(g.tipo || '');
      const escapar = (v) => String(v || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
      const opciones = tipos.map((t) => `<option value="${escapar(t.id)}">${escapar(t.nombre)}</option>`).join('');
      const { value } = await Swal.fire({ title: 'Agregar al catálogo',
        html: `<p>Tipo reportado: <b>${escapar(g.tipo)}</b></p><label>Buscar existente<input id="tipo-buscar" class="swal2-input" placeholder="Buscar tipo"></label><label>Tipo existente<select id="tipo-existente" class="swal2-input"><option value="">Crear tipo nuevo</option>${opciones}</select></label><label>Nombre del tipo<input id="tipo-nombre" class="swal2-input" value="${escapar(g.tipo)}"></label><p>También puedes cerrar esta ventana y dejar el texto reportado sin catalogar.</p>`,
        showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Dejar como texto',
        didOpen: () => {
          let secuencia = 0;
          document.getElementById('tipo-buscar').addEventListener('input', async (e) => {
            const actual = ++secuencia;
            try {
              const encontrados = await buscarTiposGasto(e.target.value);
              const select = document.getElementById('tipo-existente');
              if (actual !== secuencia || !select) return;
              select.replaceChildren(new Option('Crear tipo nuevo', ''));
              encontrados.forEach((t) => select.add(new Option(t.nombre, t.id)));
            } catch { /* Se conserva lo escrito. */ }
          });
        },
        preConfirm: () => { const tipoId = document.getElementById('tipo-existente').value;
          const nuevoNombre = document.getElementById('tipo-nombre').value.trim();
          if (!tipoId && !nuevoNombre) { Swal.showValidationMessage('Indica el nombre del tipo'); return false; }
          return { tipoId, nuevoNombre }; } });
      if (!value) return;
      setBusy(true);
      await resolverTipoGasto({ cierreId, indice, ...value });
      await cargar({ soloResultados: true });
      onChanged?.();
    } catch (err) { Swal.fire('Error', err.message || 'No se pudo catalogar el tipo.', 'error'); }
    finally { setBusy(false); }
  };

  const revisarBeneficiario = async (g, indice) => {
    try {
      const candidatos = await buscarBeneficiariosGasto(g.beneficiario || '');
      const escapar = (v) => String(v || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
      const opciones = candidatos.map((b) => `<option value="${escapar(b.id)}">${escapar(b.nombre)}</option>`).join('');
      const { value } = await Swal.fire({ title: 'Agregar al catálogo',
        html: `<p>Beneficiario reportado: <b>${escapar(g.beneficiario)}</b></p><label>Buscar en catálogo<input id="beneficiario-buscar" class="swal2-input" placeholder="Escribe otro nombre para buscar"></label><label>Beneficiario existente<select id="beneficiario-existente" class="swal2-input"><option value="">Crear beneficiario nuevo</option>${opciones}</select></label><label>Nombre<input id="beneficiario-nombre" class="swal2-input" value="${escapar(g.beneficiario)}"></label><p>Puedes dejarlo únicamente como texto reportado.</p>`,
        showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Dejar como texto',
        didOpen: () => {
          let secuencia = 0;
          document.getElementById('beneficiario-buscar').addEventListener('input', async (e) => {
            const actual = ++secuencia;
            try {
              const encontrados = await buscarBeneficiariosGasto(e.target.value);
              if (actual !== secuencia) return;
              const select = document.getElementById('beneficiario-existente');
              if (!select) return;
              select.replaceChildren(new Option('Crear beneficiario nuevo', ''));
              encontrados.forEach((b) => select.add(new Option(b.nombre, b.id)));
            } catch { /* La búsqueda se puede repetir sin perder lo escrito. */ }
          });
        },
        preConfirm: () => { const beneficiarioId = document.getElementById('beneficiario-existente').value;
          const nuevoNombre = document.getElementById('beneficiario-nombre').value.trim();
          if (!beneficiarioId && !nuevoNombre) { Swal.showValidationMessage('Indica el nombre del beneficiario'); return false; }
          return { beneficiarioId, nuevoNombre }; } });
      if (!value) return;
      setBusy(true);
      await resolverBeneficiarioGasto({ cierreId, indice, ...value });
      await cargar({ soloResultados: true });
      onChanged?.();
    } catch (err) { Swal.fire('Error', err.message || 'No se pudo catalogar el beneficiario.', 'error'); }
    finally { setBusy(false); }
  };

  // Estado de guardado del cierre: lo escrito vs lo que está en la base de datos (no lo que se ve en pantalla).
  const actualAuto = claveAuto(sistema, comentario);
  const autoSinGuardar = !!persistidoClave && actualAuto !== persistidoClave;
  const motivoAuto = autoSinGuardar ? motivoNoGuardable({ ...sistema, comentario }) : null;
  const haySinGuardar = autoSinGuardar || edicionConCambios || guardado === 'guardando';
  // Guardar cambios: guarda ya lo pendiente de guardado automático (datos del sistema y Observación).
  const guardarTodo = async () => {
    if (autoSinGuardar) {
      if (motivoAuto) { Swal.fire('Cambios sin guardar', `No se puede guardar todavía: ${motivoAuto}.`, 'warning'); return; }
      if (!(await guardarAuto({ ...sistema, comentario }))) return;
    }
  };
  // Cerrar (×, Escape, fondo, cambiar de cierre): lo que se guarda solo se guarda primero; si algo no se puede guardar, se pregunta.
  const pedirCerrar = async () => {
    const listo = autoSinGuardar && !motivoAuto ? await guardarAuto({ ...sistema, comentario }) : !autoSinGuardar;
    if (listo && !edicionConCambios) { onClose(); return; }
    const r = await Swal.fire({ title: SALIR_SIN_GUARDAR, icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Salir sin guardar', cancelButtonText: 'Seguir revisando' });
    if (r?.isConfirmed) onClose();
  };
  const cerrarRef = useRef(pedirCerrar);
  cerrarRef.current = pedirCerrar;
  // Escape cierra el detalle (no cuando hay otra ventana encima: diálogo de confirmación o formulario de liquidación).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || document.querySelector('.swal2-container') || document.querySelectorAll('[role="dialog"]').length > 1) return;
      cerrarRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  // Recargar/cerrar la pestaña o salir por un enlace de la aplicación con cambios pendientes: se avisa.
  useEffect(() => {
    marcarCambiosPendientes('cierre-detalle', haySinGuardar);
    if (!haySinGuardar) return () => marcarCambiosPendientes('cierre-detalle', false);
    const antes = (e) => { e.preventDefault(); e.returnValue = ''; };
    const enlace = (e) => {
      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank' || a.origin !== window.location.origin) return;
      if (!window.confirm(SALIR_SIN_GUARDAR)) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', antes);
    document.addEventListener('click', enlace, true);
    return () => { window.removeEventListener('beforeunload', antes); document.removeEventListener('click', enlace, true); marcarCambiosPendientes('cierre-detalle', false); };
  }, [haySinGuardar]);
  const textoGuardado = guardado === 'guardando' ? 'Guardando…'
    : guardado === 'error' && autoSinGuardar ? 'No se pudo guardar el último cambio'
    : haySinGuardar ? `Cambios sin guardar${motivoAuto ? `: ${motivoAuto}` : edicionConCambios && !autoSinGuardar ? ' en "Editar datos del cajero": usa su botón Guardar cambios' : ''}`
    : guardadoAt ? `Todos los cambios guardados · guardado automáticamente a las ${formatTime(guardadoAt)}` : 'Todos los cambios guardados';

  const { sucursales, me } = useSucursalActiva();
  const puedeExcepcion = tienePermiso(me, PERMISO_CORRECCION_EXCEPCIONAL); // solo oculta botones: la API lo valida
  const c = d?.cierre, t = c?.totales || {}, rep = c?.arqueo?.[0] || {};
  const resumenCompras = resumirCompras(d?.compras || []);
  const serv = serviciosDe(sucursales.find((x) => x.id === c?.sucursalId));
  // Cuadre y medios de pago: misma fuente que el PDF del cierre (cuadreVista).
  const vista = c ? cuadreVista(c, { servicios: serv, sistema }) : {};
  const fondo = c ? notaFondo(c, d.siguienteApertura) : null;
  const { conteoCaja, ventasIndependientes, conteoHistorico, efectivoContado: efectivoContadoVista, subtotalCajero, subtotalSistema } = vista;
  // Valores del cajero (los que se corrigen) e historial de sus correcciones.
  const cajeroActual = { efectivo: n(conteoCaja ? rep.efectivoContado : rep.efectivoManual), tarjeta: n(rep.tarjeta), pedidosYa: n(rep.pedidosYa) };
  const correccionesCajero = (d?.eventos || []).filter((e) => e.accion === 'ventas_cajero_corregidas').reverse();
  const difVista = (k, dif) => {
    if (k === 'efectivo' && !ventasIndependientes) return null;
    if (!edicion) return dif;
    const cajero = parseDinero(edicion.valores[k]), sis = parseDinero(sistema[k]);
    return cajero === null || sis === null ? dif : sumarDinero(cajero, -sis);
  };
  const resultado = c ? resultadoCierre(c) : null, revision = c ? revisionCierre(c) : '';
  // Marcar como revisado: acepta y documenta el resultado (no lo cambia). Distinto de "Habilitar corrección".
  const marcarRevisado = async () => {
    const { value: comentario, isConfirmed } = await Swal.fire({ title: 'Marcar como revisado', input: 'textarea',
      text: `${textoResultado(resultado)}. El resultado no cambia; queda documentado quién lo revisó y por qué.`,
      inputLabel: ['Cuadrado', 'Ventas coinciden'].includes(resultado.tipo) ? 'Observación (opcional)' : `Explica ${resultado.tipo.toLowerCase()}`, inputAttributes: { maxlength: '1000' },
      inputValidator: (v) => (!['Cuadrado', 'Ventas coinciden'].includes(resultado.tipo) && !String(v || '').trim() ? 'La observación es obligatoria' : undefined),
      showCancelButton: true, confirmButtonText: 'Marcar como revisado', cancelButtonText: 'Cancelar' });
    if (!isConfirmed) return;
    setBusy(true);
    try { await revisarCierre(cierreId, String(comentario || '').trim()); await cargar(); onChanged?.(); }
    catch (err) { Swal.fire('No se pudo marcar', err.message || '', 'error'); }
    finally { setBusy(false); }
  };
  return (
    <FondoModal onClose={pedirCerrar} confirmar={false} esc={false}>
      <section className="fx-modal" role="dialog" aria-modal="true" aria-label="Detalle del cierre" onMouseDown={(e) => e.stopPropagation()}>
        <header>
          <div>
            <h2>Cierre {c ? `${fechaCorta(c.fecha)} · ${nombreTurno(c.turnoId)}` : ''}</h2>
            {c && <p>{d.sucursal} · Cajero: {c.username} · <span className={`fx-badge fx-badge--${claseResultado(resultado, revision)}`}>{textoResultado(resultado)}</span>{' '}
              <span className={`fx-badge fx-badge--${claseRevision[revision]}`}>{revision}</span></p>}
          </div>
          {/* PDF: Ventas (cajero vs sistema) → Compras → Resumen de compras, con los mismos valores de esta pantalla. */}
          {d && <div style={{ marginLeft: 'auto' }}><button type="button" className="fx-btn" onClick={() => exportCierreDetallePdf(d, { servicios: serv })}>Exportar PDF</button></div>}
          <button type="button" aria-label="Cerrar" onClick={pedirCerrar}>×</button>
        </header>
        {error && <p role="alert">{error}</p>}
        {/* Estado real de guardado del cierre: Cambios sin guardar / Guardando… / Todos los cambios guardados. */}
        {d && <div className={`cd-guardado${haySinGuardar || (guardado === 'error' && autoSinGuardar) ? ' cd-guardado--pend' : ''}`} role="status" aria-live="polite">
          <span>{textoGuardado}</span>
          {/* Corregir datos del cajero tiene su propio Guardar cambios (con motivo); este guarda lo de guardado automático. */}
          {autoSinGuardar && guardado !== 'guardando' && <button type="button" className="fx-btn fx-btn--primary" disabled={busy} onClick={guardarTodo}>Guardar cambios</button>}
        </div>}
        {/* Revisión administrativa: independiente del resultado (que se conserva como historial). */}
        {c && <div className="cd-revision">
          {c.revisadoAt ? <span><b>Revisado</b> por {c.revisadoPor} · {formatDateTime(c.revisadoAt)}
            {c.revisionComentario && <> — {c.revisionComentario}</>}</span>
            : <span className="cd-revision-pend">{revision === 'En corrección' ? 'En corrección: se revisa al volver a cerrarse.' : resultado.tipo === 'Sin resultado' ? 'Faltan datos del sistema para calcular el resultado del cierre.' : ['Cuadrado', 'Ventas coinciden'].includes(resultado.tipo) ? `${resultado.tipo}: no requiere explicación.` : `Pendiente de revisar: explica ${resultado.tipo.toLowerCase()} para darla por atendida.`}</span>}
          {c.estado === 'Cerrado' && revision === 'Pendiente de revisar' && resultado.tipo !== 'Sin resultado' && <button type="button" className="fx-btn fx-btn--primary" disabled={busy} onClick={marcarRevisado}>Marcar como revisado</button>}
        </div>}
        {c?.conciliacionPendiente && <div className="cd-conciliacion" role="alert">
          <div><b>Conciliación pendiente</b>
            <small>Efectivo a liquidar: {toMoney(c.conciliacionPendiente.disponibleAntes)} → {toMoney(c.conciliacionPendiente.disponibleDespues)} · Ya liquidado: {toMoney(c.conciliacionPendiente.aplicado)}
              {c.conciliacionPendiente.excedente > 0 && <> · Liquidado de más: {toMoney(c.conciliacionPendiente.excedente)}</>}</small></div>
          {puedeExcepcion && <button type="button" className="fx-btn" disabled={busy} onClick={conciliar}>Marcar como conciliado</button>}
        </div>}
        {!d && !error && <p className="fx-empty">Cargando…</p>}
        {d && <>
          {(d.solicitudes || []).filter((s) => s.estado === 'Pendiente').map((s) => (
            <div key={s.id} className="fx-card solcor-pendiente" role="alert">
              <div><b>Solicitud de corrección</b> de {s.solicitadoPor} · {hora(s.solicitadoAt)}<small>Motivo: {s.motivo}</small></div>
              <div className="fx-actions" style={{ marginTop: 0 }}>
                <button type="button" className="fx-btn" onClick={() => responder(s, false)} disabled={busy}>Rechazar</button>
                <button type="button" className="fx-btn fx-btn--primary" onClick={() => responder(s, true)} disabled={busy}>Aprobar</button>
              </div>
            </div>))}
          {/* Resumen del turno en una fila (el resultado financiero ya está en el encabezado). */}
          <dl className="cd-turno">
            <div><dt>Apertura</dt><dd>{soloHora(d.apertura?.inicioAt || c.inicioAt)}</dd></div>
            <div><dt>Cierre</dt><dd>{soloHora(c.cierreAt)}</dd></div>
          </dl>

          <div className="fx-card" style={{ marginBottom: 12 }}>
            <div className="cd-ventas-hd">
              <h3 style={{ margin: 0, fontSize: 17 }}>Ventas: reportado por el cajero vs sistema</h3>
              {c.estado === 'Cerrado' && !edicion && <button type="button" className="fx-btn" disabled={busy}
                onClick={() => iniciarEdicion(cajeroActual)}>Editar datos del cajero</button>}
            </div>
            {/* Efectivo: apertura + ventas POS (sistema) contra conteo físico + salidas (cajero). La liquidación usa el conteo, nunca el esperado. */}
            <table className="fx-table cd-calculo cd-cuadre" aria-label="Cuadre de efectivo">
              <thead><tr><th>Concepto</th><th className="num">Cajero</th><th className="num">Sistema</th></tr></thead>
              <tbody>
              <tr><td>Apertura</td><td className="num">—</td><td className="num">{toMoney(t.efectivoInicial)}</td></tr>
              <tr><td>Ventas en efectivo del sistema (POS)</td><td className="num">—</td><td className="num"><MoneyInput className="mi--tabla"
                style={{ width: 130, marginLeft: 'auto' }} aria-label="Ventas en efectivo según el sistema"
                value={sistema.efectivo} onBlur={alSalir} onChange={(v) => cambiarSistema('efectivo', v)} /></td></tr>
              {n(t.cobrosSucursalesEfectivo) > 0 && <tr><td>+ Cobros en efectivo a otras taquerías</td><td className="num">—</td><td className="num">{toMoney(t.cobrosSucursalesEfectivo)}</td></tr>}
              {efectivoContadoVista != null && <tr><td>Efectivo contado en caja</td><td className="num">{edicion && (conteoCaja || conteoHistorico)
                ? <MoneyInput className="mi--tabla" aria-label="Efectivo contado en caja" value={edicion.valores.efectivo}
                    onChange={(v) => setEdicion({ ...edicion, valores: { ...edicion.valores, efectivo: v } })} />
                : toMoney(efectivoContadoVista)}</td><td className="num">—</td></tr>}
              <tr><td>+ Compras, gastos y pagos en efectivo</td><td className="num">{toMoney(t.salidasEfectivo)}</td><td className="num">—</td></tr>
              {ventasIndependientes && <tr><td>Ventas en efectivo reportadas (formato anterior)</td>
                <td className="num">{edicion ? <MoneyInput className="mi--tabla" aria-label="Ventas en efectivo reportadas por cajero"
                  value={edicion.valores.efectivo} onChange={(v) => setEdicion({ ...edicion, valores: { ...edicion.valores, efectivo: v } })} />
                  : toMoney(t.ventasEfectivoReportadas)}</td><td className="num">—</td></tr>}
              {conteoHistorico && <tr><td colSpan={3}><small className="fx-empty">Este cierre fue registrado con una versión anterior del sistema.</small></td></tr>}
              <tr className="cd-sub"><td><b>Subtotal efectivo</b></td><td className="num"><b>{subtotalCajero == null ? '—' : toMoney(subtotalCajero)}</b></td>
                <td className="num"><b>{subtotalSistema == null ? 'Pendiente' : toMoney(subtotalSistema)}</b></td></tr>
              <tr className="cd-sub cd-cuadre__resultado"><td colSpan={2}><b>Resultado</b></td>
                <td className="num"><b>{vista.resultadoTexto}</b></td></tr>
              {n(c.faltantePagado) > 0 && <tr><td>Faltante pagado</td><td className="num">{toMoney(c.faltantePagado)}</td><td className="num">—</td></tr>}
            </tbody></table>
            {/* Qué pasó con el fondo dejado en caja: nota de trazabilidad (no otra fila contable). */}
            {fondo && <p className={`cd-nota-fondo${fondo.alerta ? ' cd-nota-fondo--alerta' : ''}`}>{fondo.texto}</p>}
            <table className="fx-table fx-table--stack" style={{ marginTop: 12 }}>
              <thead><tr><th>Medio</th><th className="num">Cajero</th><th className="num">Sistema</th><th className="num">Diferencia</th><th className="num">Comisión</th><th className="num">Neto a recibir</th></tr></thead>
              <tbody>
                {vista.medios.map(({ label, k, cajero, diferencia: dif, pct, comision, neto }) => (
                    <tr key={k}><td data-label="Medio">{label}</td>
                      <td data-label="Cajero" className="num">{edicion
                        ? <MoneyInput className="mi--tabla" aria-label={`${label} reportado por el cajero`}
                            value={edicion.valores[k]} onChange={(v) => setEdicion({ ...edicion, valores: { ...edicion.valores, [k]: v } })} />
                        : toMoney(cajero)}</td>
                      <td data-label="Sistema" className="num"><MoneyInput className="mi--tabla" style={{ width: 130, marginLeft: 'auto' }}
                        aria-label={`${label} según sistema`}
                        value={sistema[k]} onBlur={alSalir} onChange={(v) => cambiarSistema(k, v)} /></td>
                      <td data-label="Diferencia" className="num"><Dif v={difVista(k, dif)} /></td>
                      {/* Neto que deberían depositar el procesador de tarjetas / PedidosYa: bruto − comisión. */}
                      <td data-label="Comisión" className="num">{pct == null ? <span className="fx-empty">Pendiente</span> : <>−{toMoney(comision)} <small className="fx-empty">({pct}%)</small></>}</td>
                      <td data-label="Neto a recibir" className="num">{neto == null ? <span className="fx-empty">Pendiente</span> : <b>{toMoney(neto)}</b>}</td></tr>))}
              </tbody>
              <tfoot><tr><td><b>Total ventas del sistema</b></td><td className="num">—</td>
                <td className="num"><b>{t.ventasTotales != null ? toMoney(t.ventasTotales) : <span className="fx-empty">Pendiente</span>}</b></td><td colSpan={3} /></tr></tfoot>
            </table>
            {edicion && <div className="cd-ventas-edicion">
              <label>Motivo de la corrección
                <textarea rows={2} maxLength={500} value={edicion.motivo} placeholder="Ej.: El cajero ingresó incorrectamente el total de tarjeta."
                  onChange={(e) => setEdicion({ ...edicion, motivo: e.target.value })} /></label>
              <small className="fx-empty" style={{ padding: 0 }}>Solo cambia lo reportado por el cajero; los datos del sistema no se tocan. Si el cierre estaba revisado, vuelve a "Pendiente de revisar".</small>
              <div className="fx-actions"><button type="button" className="fx-btn" disabled={guardandoEdicion} onClick={cancelarEdicion}>Cancelar</button>
                <button type="button" className="fx-btn fx-btn--primary" disabled={guardandoEdicion} onClick={guardarVentasCajero}>{guardandoEdicion ? 'Guardando…' : 'Guardar cambios'}</button></div>
            </div>}
            {correccionesCajero.length > 0 && <details className="cd-ventas-hist">
              <summary>Ver historial de correcciones ({correccionesCajero.length})</summary>
              <div className="ces-scroll"><table className="fx-table fx-table--stack">
                <thead><tr><th>Fecha/hora</th><th>Administrador</th><th>Campo</th><th className="num">Anterior</th><th className="num">Nuevo</th><th>Motivo</th></tr></thead>
                <tbody>{correccionesCajero.flatMap((e, i) => (e.detalle?.cambios || []).map((m, j) => <tr key={`${i}-${j}`}>
                  <td data-label="Fecha/hora">{hora(e.at)}</td><td data-label="Administrador">{e.actor}</td><td data-label="Campo">{m.campo}</td>
                  <td data-label="Anterior" className="num">{toMoney(m.anterior)}</td><td data-label="Nuevo" className="num">{toMoney(m.nuevo)}</td>
                  <td data-label="Motivo">{e.detalle?.motivo}</td></tr>))}</tbody>
              </table></div>
            </details>}
            {/* Imagen del cierre de Tarjeta y de PedidosYa/POS que adjuntó el cajero (respaldo visual opcional). */}
            {(n(rep.tarjeta) > 0 || n(rep.pedidosYa) > 0 || rep.comprobanteTarjeta || rep.comprobantePedidosYa) && <p style={{ margin: '8px 0 0', fontSize: 13 }}>Imagen del cierre:
              {[(n(rep.tarjeta) > 0 || rep.comprobanteTarjeta) && ['Tarjeta', rep.comprobanteTarjeta], (n(rep.pedidosYa) > 0 || rep.comprobantePedidosYa) && ['PedidosYa/POS', rep.comprobantePedidosYa]]
                .filter(Boolean).map(([medio, url], i) => <React.Fragment key={medio}>{i > 0 && ' · '} {medio} {url ? <ComprobanteLink url={url} texto="ver imagen" /> : <span className="fx-empty" style={{ padding: 0 }}>Sin comprobante</span>}</React.Fragment>)}</p>}
            {vista.netoSobre && <small className="fx-empty">Neto calculado sobre {vista.netoSobre}. Compáralo con el depósito que hace el procesador de tarjetas o PedidosYa.</small>}
            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600, marginTop: 10 }}>Observación del cierre
              <textarea rows={2} value={comentario} maxLength={1000} onBlur={alSalir} onChange={(e) => cambiarComentario(e.target.value)} style={{ border: '1px solid var(--line-strong)', borderRadius: 7, padding: 8, font: 'inherit' }} /></label>
            <small className="fx-empty" role="status" style={{ display: 'block', marginTop: 8, padding: 0 }}>
              {/* Los datos del sistema y la Observación se guardan automáticamente; el estado real está arriba. */}
              {autoSinGuardar ? (motivoAuto ? `Sin guardar: ${motivoAuto}.` : guardado === 'guardando' ? 'Guardando…' : 'Se guardará automáticamente.')
                : c.sistemaRegistradoAt ? `Datos del sistema: guardados automáticamente · ${c.sistemaRegistradoPor?.username || ''} · ${hora(c.sistemaRegistradoAt)}`
                : 'Los datos del sistema y la observación se guardan automáticamente.'}
            </small>
          </div>

          {/* Compras: encabezado (proveedor, factura, fecha, tipo, pago) → productos → total abajo a la derecha. */}
          <div className="fx-card" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Compras ({d.compras.length})</h3>
            {d.compras.length ? d.compras.map((k) => {
              const sinCatalogo = !k.proveedorId || k.items.some((i) => !i.productoId && !i.descripcionGeneral);
              return (
              <article key={k.id} className="cd-reg">
                <header className="cd-reg-hd">
                  <div className="cd-reg-tit"><b>{k.proveedorNombre}</b>{sinCatalogo && <span className="fx-badge fx-badge--hoy">Pendiente de catálogo</span>}</div>
                  <div className="cd-reg-meta">
                    <span>Fact. {k.documento || '—'}</span><span>{fechaCorta(k.fecha)}</span><span>{k.tipo}</span>
                    <span>{formaPagoCompra(k)}</span>
                  </div>
                  <div className="cd-reg-aux">
                    {!k.proveedorId && <button type="button" className="fx-link" disabled={busy} onClick={() => revisarProveedor(k)}>Agregar proveedor al catálogo</button>}
                    {k.archivoUrl ? <ComprobanteLink url={k.archivoUrl} texto="Ver comprobante" /> : <span className="fx-empty" style={{ padding: 0 }}>Sin comprobante</span>}
                  </div>
                </header>
                {/* Precio unitario calculado (total ÷ cantidad): solo para análisis de Administración. */}
                <table className="fx-table fx-table--stack cd-reg-tabla">
                  <thead><tr><th>Producto</th><th className="num">Cantidad</th><th className="num">Precio total</th><th className="num">Unitario</th><th>Catálogo</th></tr></thead>
                  <tbody>{k.items.map((i) => <tr key={i.id || i.producto}>
                    <td data-label="Producto">{i.producto}</td>
                    <td data-label="Cantidad" className="num">{cantidadItem(i)}</td>
                    <td data-label="Precio total" className="num">{toMoney(i.subtotal)}</td>
                    <td data-label="Unitario" className="num cd-reg-sec">{unitarioItem(i)}</td>
                    <td data-label="Catálogo" className="cd-reg-sec">{i.productoId ? '✓' : i.descripcionGeneral ? '—' : <button type="button" className="fx-link" disabled={busy} onClick={() => revisarProducto(i)}>Agregar</button>}</td>
                  </tr>)}</tbody>
                </table>
                {k.observaciones && <p className="cd-reg-obs">{k.observaciones}</p>}
                <div className="cd-reg-total"><span>Total compra</span><b>{toMoney(k.total)}</b></div>
              </article>); }) : <p className="fx-empty">Sin compras.</p>}
            {d.compras.length > 0 && <div className="cd-resumen-compras" aria-label="Resumen de compras">
              <strong>Resumen de compras</strong>
              <div className="cd-resumen-compras__fila cd-resumen-compras__fila--caja"><span>Compras en efectivo <small>(salida de caja)</small></span><b>{toMoney(resumenCompras.efectivo)}</b></div>
              <div className="cd-resumen-compras__fila"><span>Compras a crédito <small>(cuentas por pagar)</small></span><b>{toMoney(resumenCompras.credito)}</b></div>
              {resumenCompras.otros > 0 && <div className="cd-resumen-compras__fila"><span>Otros pagos / fuera de caja</span><b>{toMoney(resumenCompras.otros)}</b></div>}
              <div className="cd-resumen-compras__fila cd-resumen-compras__fila--total"><span>Total comprado <small>(incluye crédito y pagos fuera de caja)</small></span><b>{toMoney(resumenCompras.total)}</b></div>
            </div>}
          </div>

          {/* Gastos: encabezado (tipo, beneficiario, fecha, forma) → detalle → total abajo a la derecha. */}
          <div className="fx-card" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Gastos y pagos registrados por el cajero ({(c.gastos || []).length})</h3>
            {(c.gastos || []).length ? c.gastos.map((g, i) => {
              const tipoPend = !g.tipoId, benPend = g.tipo !== 'Pago a proveedor' && !g.beneficiarioId;
              const detalle = [g.descripcion, g.referencia && `Ref. ${g.referencia}`].filter(Boolean).join(' · ');
              return (
              <article key={i} className="cd-reg">
                <header className="cd-reg-hd">
                  <div className="cd-reg-tit"><b>{g.tipo || g.categoria || '—'}</b>{(tipoPend || benPend) && <span className="fx-badge fx-badge--hoy">Pendiente de catálogo</span>}</div>
                  <div className="cd-reg-meta">
                    <span>Beneficiario: {g.beneficiario || '—'}</span><span>{hora(g.registradoAt)}</span><span>{g.formaPago || 'Efectivo'}</span>
                  </div>
                  <div className="cd-reg-aux">
                    {tipoPend && <button type="button" className="fx-link" disabled={busy} onClick={() => revisarTipo(g, i)}>Agregar tipo al catálogo</button>}
                    {benPend && <button type="button" className="fx-link" disabled={busy} onClick={() => revisarBeneficiario(g, i)}>Agregar beneficiario al catálogo</button>}
                    {g.fileUrl ? <ComprobanteLink url={g.fileUrl} texto="Ver comprobante" /> : <span className="fx-empty" style={{ padding: 0 }}>Sin comprobante</span>}
                  </div>
                </header>
                <dl className="cd-reg-cuerpo">
                  <div><dt>Descripción</dt><dd>{detalle || '—'}</dd></div>
                  {g.observacion && <div><dt>Observación</dt><dd>{g.observacion}</dd></div>}
                  {(g.aplicaciones || []).length > 0 && <div><dt>Facturas</dt><dd>{g.aplicaciones.map((a) => `${a.documento || 'Fact.'}: ${toMoney(a.saldoAnterior)} → ${toMoney(a.saldoNuevo)}`).join(' · ')}</dd></div>}
                </dl>
                <div className="cd-reg-total"><span>Total gasto</span><b>{toMoney(g.cantidad)}</b></div>
              </article>); }) : <p className="fx-empty">Sin gastos.</p>}
          </div>

          {/* Salidas de productos a otras taquerías: concepto aparte; generan deuda y no afectan el efectivo. */}
          <div className="fx-card" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Salidas de productos a otras taquerías ({(d.salidasProductos || []).filter((x) => x.estado !== 'Anulada').length})</h3>
            {(d.salidasProductos || []).length ? d.salidasProductos.map((x) => {
              const items = x.items || [];
              const sinValor = items.length > 0 && items.every((i) => i.total == null);
              return (
              <article key={x.id} className="cd-reg" style={x.estado === 'Anulada' ? { opacity: 0.6 } : undefined}>
                <header className="cd-reg-hd">
                  <div className="cd-reg-tit"><b>{x.destino}</b>{x.estado === 'Anulada' && <span className="fx-badge">Anulada</span>}</div>
                  <div className="cd-reg-meta">
                    <span>{hora(x.fechaAt)}</span><span>Entregó: {x.entregadoPor || '—'}</span><span>Recibió: {x.recibidoPor || '—'}</span><span>Registró: {x.usuario || '—'}</span>
                  </div>
                  <div className="cd-reg-aux">
                    {x.reciboUrl ? <ComprobanteLink url={x.reciboUrl} texto="Ver comprobante" /> : <span className="fx-empty" style={{ padding: 0 }}>Sin comprobante</span>}
                    <span className="cd-reg-sec">Ref. {x.id}</span>
                  </div>
                </header>
                {/* Sin costo conocido: precio y total quedan en "—" (valor pendiente), nunca Q0.00. */}
                {items.length > 0 && <table className="fx-table fx-table--stack cd-reg-tabla">
                  <thead><tr><th>Producto / Descripción</th><th className="num">Cantidad</th><th className="num">Precio</th><th className="num">Total</th></tr></thead>
                  <tbody>{items.map((i) => <tr key={i.id || i.producto}>
                    <td data-label="Producto">{i.producto}{i.detalleCantidad && <small className="cd-reg-sec" style={{ display: 'block' }}>{i.detalleCantidad}</small>}</td>
                    <td data-label="Cantidad" className="num">{cantidadItem(i)}</td>
                    <td data-label="Precio" className="num">{i.valorUnitario == null ? '—' : toMoney(i.valorUnitario)}</td>
                    <td data-label="Total" className="num">{i.total == null ? '—' : toMoney(i.total)}</td>
                  </tr>)}</tbody>
                </table>}
                {x.observacion && <p className="cd-reg-obs">{x.observacion}</p>}
                <div className="cd-reg-total"><span>Total salida{x.productosPendientes > 0 && !sinValor && <small style={{ display: 'block', fontWeight: 400, color: 'var(--warn-text)' }}>+ {x.productosPendientes} con valor pendiente</small>}</span>
                  <b>{sinValor ? 'Valor pendiente' : toMoney(x.total)}</b></div>
              </article>); }) : <p className="fx-empty">Sin salidas de productos.</p>}
            <small className="fx-empty" style={{ display: 'block', padding: '4px 0 0' }}>Informativo: no sale efectivo de la caja; cada salida genera una cuenta por cobrar en Cuentas entre taquerías.</small>
          </div>

          {/* Instrucciones (qué debe hacerse con el dinero) y Liquidación del efectivo (qué se hizo realmente). */}
          <div style={{ marginBottom: 12 }}><LiquidacionSeccion cierre={c} sucursal={d.sucursal} onChanged={onChanged} enDetalle /></div>

          {/* Historial de correcciones: solicitud → respuesta → corrección realizada → cierre bloqueado otra vez. */}
          {(d.solicitudes || []).length > 0 && (
            <div className="fx-card" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Historial de correcciones</h3>
              <ol className="solcor-historial">{d.solicitudes.map((s) => (
                <li key={s.id}>
                  <div><b>Solicitud</b> · {s.solicitadoPor} · {hora(s.solicitadoAt)} — {s.motivo}</div>
                  {s.respondidoAt && <div><b>{s.estado === 'Rechazada' ? 'Rechazada' : 'Aprobada'}</b> · {s.respondidoPor} · {hora(s.respondidoAt)}{s.comentarioRespuesta ? ` — ${s.comentarioRespuesta}` : ''}</div>}
                  {s.corregidoAt && <div><b>Corrección realizada</b> · {s.corregidoPor} · {hora(s.corregidoAt)} · cierre bloqueado nuevamente
                    {(s.cambios || []).length ? <ul>{s.cambios.map((x, i) => <li key={i}>{x.campo}: {typeof x.antes === 'number' ? toMoney(x.antes) : x.antes} → {typeof x.despues === 'number' ? toMoney(x.despues) : x.despues}</li>)}</ul>
                      : <small style={{ display: 'block' }}>Sin cambios en montos.</small>}</div>}
                  {s.estado === 'Pendiente' && <div className="fx-empty" style={{ padding: 0 }}>Esperando respuesta del administrador.</div>}
                  {s.estado === 'Aprobada' && !s.corregidoAt && <div className="fx-empty" style={{ padding: 0 }}>Habilitado: esperando la corrección del cajero.</div>}
                </li>))}</ol>
            </div>)}

          <div className="fx-actions">
            <button className="fx-btn" onClick={() => exportSingleCuadrePdf(c, d.sucursal, formatDate)}>Descargar PDF</button>
            {c.estado === 'Cerrado' && <button className="fx-btn" onClick={habilitar} disabled={busy}>Habilitar corrección</button>}
            {c.estado === 'Corrección' && puedeExcepcion && onCorregir && <button className="fx-btn fx-btn--primary" onClick={() => onCorregir(c.id)}>Corregir cierre</button>}
          </div>
        </>}
      </section>
    </FondoModal>
  );
}
